/**
 * Which FROM source a result column comes from when no select item names it
 * (a `*` / `t.*` column) — the source side of `resultColumnWhereField`: the
 * driver's provenance, checked against the column names each source exposes.
 *
 * The provenance names the BASE column a value was read from. A derived table
 * or CTE over that table renames it — PostgreSQL / SQL Server / Oracle report
 * `x` of `SELECT * FROM (SELECT id AS x FROM t) s` as `t.id`, MySQL as `t.x` —
 * while the statement's WHERE only sees `s.x`. So the provenance places a
 * column only through a FROM base table that exposes it under the result's
 * name; otherwise the column is the one source that can hold that name.
 */

import type { ParseOptions } from '../../../sql-grammar/model';
import { readQualifiedName } from '../../../sql-resolver/scope/identifiers';
import { querySourceOf, sourceColumns, type QuerySource } from '../../../sql-resolver/scope/querySources';
import { foldFor } from '../../../sql-resolver/service/schemaProjectionHelpers';
import type { FromItem, QueryStructure, SchemaSnapshot, SelectItem } from '../../../sql-resolver/types';

/** The provenance fields of a result column. */
export type ColumnProvenance = {
  readonly name: string;
  readonly sourceSchema?: string;
  readonly sourceTable?: string;
  readonly sourceColumn?: string;
};

export function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/** A select item's output name — its alias, else a plain column reference's name — and whether it is a column reference. */
export function selectItemName(item: SelectItem, options: ParseOptions): { name?: string; isColumnRef: boolean } {
  const text = item.expressionText.trim();
  const ref = readQualifiedName(text, 0, text.length, options);
  const isColumnRef = !!item.resolved || (ref !== null && ref.range.endOffset === text.length);
  return { name: item.alias ?? (isColumnRef && ref ? ref.qualified.name : undefined), isColumnRef };
}

/**
 * The FROM base table the driver's provenance binds the column to: `null` when
 * it does not place it (no provenance, a renamed column, no such FROM table, or
 * a table whose snapshot columns lack the name), `'unknown'` when it cannot
 * tell — the table is there twice (a self-join), or the snapshot is not known
 * yet and another source surely exposes the name (MySQL's `t.x` above).
 */
export function provenanceSource(
  structure: QueryStructure,
  options: ParseOptions,
  column: ColumnProvenance,
  snapshot: SchemaSnapshot | null | undefined,
): QuerySource | 'unknown' | null {
  if (!column.sourceTable) return null;
  if (column.sourceColumn !== undefined && !sameName(column.sourceColumn, column.name)) return null;
  const fold = foldFor(options.engine);
  const table = fold(column.sourceTable);
  const schema = column.sourceSchema ? fold(column.sourceSchema) : undefined;
  const items = fromItemsOf(structure);
  const bound = items.filter((item) => {
    const s = querySourceOf(item, options.engine);
    return s.kind === 'relation' && s.relationKey === table && (schema === undefined || s.schemaKey === undefined || s.schemaKey === schema);
  });
  if (bound.length !== 1) return bound.length === 0 ? null : 'unknown';
  const names = exposedNames(bound[0]!, options, snapshot);
  if (names !== null) return names.some((n) => sameName(n, column.name)) ? querySourceOf(bound[0]!, options.engine) : null;
  const elsewhere = items.some((item) => item !== bound[0] && exposedNames(item, options, snapshot)?.some((n) => sameName(n, column.name)));
  return elsewhere ? 'unknown' : querySourceOf(bound[0]!, options.engine);
}

/**
 * The FROM source that can hold a column named `name` when every other one
 * cannot. A source whose column names are not all known might hold it, so it
 * counts as a holder: a base table the snapshot lacks, a CTE without a column
 * list, a derived table with a `*` or an unnamed expression in its select list,
 * a table function.
 */
export function soleSourceExposing(
  structure: QueryStructure,
  options: ParseOptions,
  name: string,
  snapshot: SchemaSnapshot | null | undefined,
): QuerySource | null {
  const holders = fromItemsOf(structure).filter((item) => {
    const names = exposedNames(item, options, snapshot);
    return names === null || names.some((n) => sameName(n, name));
  });
  return holders.length === 1 ? querySourceOf(holders[0]!, options.engine) : null;
}

function fromItemsOf(structure: QueryStructure): FromItem[] {
  return [...(structure.fromItems ?? []), ...(structure.joins ?? []).map((j) => j.right)];
}

/**
 * The non-base-table FROM sources (a derived table, a CTE, a table function)
 * whose KNOWN column list contains `name` — a base table's columns are
 * unknown without a schema snapshot, so it is never counted here even when it
 * genuinely exposes the name; callers that cannot check it treat it as
 * possibly exposing it (leave it enabled) rather than as excluded.
 */
export function nonRelationSourcesExposing(structure: QueryStructure, options: ParseOptions, name: string): QuerySource[] {
  return fromItemsOf(structure)
    .filter((item) => querySourceOf(item, options.engine).kind !== 'relation' && exposedNames(item, options, null)?.some((n) => sameName(n, name)))
    .map((item) => querySourceOf(item, options.engine));
}

/** The column names a FROM item exposes; null when they are not all known. */
function exposedNames(item: FromItem, options: ParseOptions, snapshot: SchemaSnapshot | null | undefined): string[] | null {
  const { target } = item;
  if (target.kind === 'cte-ref') return target.cte.columns?.map((c) => c.qualified.name) ?? null;
  if (target.kind === 'relation') {
    const columns = sourceColumns(querySourceOf(item, options.engine), snapshot);
    return columns.length > 0 ? columns.map((c) => c.qualified.name) : null;
  }
  if (target.kind !== 'derived-table') return null;
  const names: string[] = [];
  for (const selectItem of target.subquery.selectItems ?? []) {
    const text = selectItem.expressionText.trim();
    const name = text === '*' || text.endsWith('.*') ? undefined : selectItemName(selectItem, options).name;
    if (name === undefined) return null;
    names.push(name);
  }
  return names;
}
